import { Check, ChevronDown, Pencil, X } from 'lucide-react'
import { useState } from 'react'
import { Markdown } from '@/components/Markdown'
import { Button } from '@/components/ui/button'
import { Panel, PanelHeader } from '@/components/ui/display'
import { Textarea } from '@/components/ui/field'
import { AddNote } from '@/features/meetings/AddNote'
import { NoteVisibility } from '@/features/meetings/NoteVisibility'
import type { MeetingNote } from '@/data/types'
import { cn } from '@/lib/utils'

const MAX_LENGTH = 20_000

/** The first line of a note with the markdown taken off, for a folded row. */
function preview(body: string): string {
  const line = body.split('\n').find((value) => value.trim().length > 0) ?? ''
  return line
    .replace(/^\s*(?:[-+*]|\d+\.)\s+/, '')
    .replace(/[*_`#>]/g, '')
    .trim()
}

/**
 * What the recipients will see.
 *
 * Every note is on the list, included or not, with the SAME control the
 * meetings list uses for the same decision — a note held back here is held back
 * everywhere, and it would be a strange product where that read as a checkbox
 * in one place and an Internal/External toggle in the other.
 *
 * Folded rows carry a line of their own text rather than only a title. The
 * question being answered is "is this alright to send", and a list of five
 * template names answers it only after five clicks.
 */
export function SharedNoteList({
  notes,
  onToggleShared,
  onEdit,
  onExtract,
  busy,
}: {
  notes: MeetingNote[]
  onToggleShared: (noteId: string, shared: boolean) => void
  onEdit: (noteId: string, body: string) => void
  onExtract: (templateId: string) => void
  busy?: boolean
}) {
  const [expanded, setExpanded] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const shared = notes.filter((note) => note.sharedExternally).length

  return (
    <Panel>
      <PanelHeader
        title="What they will see"
        meta={notes.length > 0 ? `${shared} of ${notes.length} shared` : undefined}
        actions={
          <AddNote
            notes={notes}
            modes={['template']}
            busy={busy}
            onAddFromTemplate={onExtract}
            onAddPrompt={() => {}}
            onAddBlank={() => {}}
          />
        }
      />

      {notes.length === 0 ? (
        <p className="px-3 py-8 text-center text-2xs text-[var(--color-fg-subtle)]">
          Nothing was written up from this meeting yet.
        </p>
      ) : (
        <ul className="divide-y divide-[var(--color-border-subtle)]">
          {notes.map((note) => {
            const open = expanded === note.id
            const line = preview(note.body)

            return (
              <li key={note.id}>
                <div className="flex items-center gap-2 px-3 py-2">
                  <NoteVisibility
                    title={note.title}
                    shared={note.sharedExternally}
                    busy={busy}
                    onChange={(next) => onToggleShared(note.id, next)}
                  />

                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => {
                      setExpanded(open ? null : note.id)
                      setEditing(null)
                    }}
                    className="flex min-w-0 flex-1 items-baseline gap-2 text-left outline-none"
                  >
                    <span
                      className={cn(
                        'shrink-0 truncate text-xs font-medium',
                        // A held-back note stays legible but stops competing
                        // with the ones that are actually going out.
                        note.sharedExternally
                          ? 'text-[var(--color-fg)]'
                          : 'text-[var(--color-fg-muted)]'
                      )}
                    >
                      {note.title}
                    </span>
                    {!open && line ? (
                      <span className="min-w-0 flex-1 truncate text-2xs text-[var(--color-fg-subtle)]">
                        {line}
                      </span>
                    ) : null}
                    <ChevronDown
                      className={cn(
                        'ml-auto size-3 shrink-0 text-[var(--color-fg-subtle)] transition-transform duration-100',
                        open && 'rotate-180'
                      )}
                    />
                  </button>
                </div>

                {open ? (
                  <div className="px-3 pb-3">
                    {editing === note.id ? (
                      <NoteEditor
                        note={note}
                        onCancel={() => setEditing(null)}
                        onSave={(body) => {
                          onEdit(note.id, body)
                          setEditing(null)
                        }}
                      />
                    ) : (
                      <div className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-2.5">
                        {note.body ? (
                          <Markdown className="prose-chat text-xs text-[var(--color-fg)]">
                            {note.body}
                          </Markdown>
                        ) : (
                          <p className="text-2xs text-[var(--color-fg-subtle)]">
                            Nothing written yet.
                          </p>
                        )}
                        <div className="mt-2 flex justify-end">
                          <Button
                            size="xs"
                            variant="ghost"
                            disabled={busy}
                            onClick={() => setEditing(note.id)}
                          >
                            <Pencil className="size-3" />
                            Edit
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </Panel>
  )
}

/** The same editor the meetings list uses, so a correction reads the same here. */
function NoteEditor({
  note,
  onSave,
  onCancel,
}: {
  note: MeetingNote
  onSave: (body: string) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState(note.body)

  return (
    <div className="space-y-2">
      <Textarea
        value={draft}
        rows={10}
        maxLength={MAX_LENGTH}
        aria-label={`${note.title} text`}
        className="font-mono text-xs"
        autoFocus
        onChange={(event) => setDraft(event.target.value)}
      />
      <div className="flex items-center justify-between">
        <span className="text-2xs text-[var(--color-fg-subtle)]">
          {draft.length.toLocaleString()} / {MAX_LENGTH.toLocaleString()}
        </span>
        <div className="flex gap-1.5">
          <Button size="sm" variant="ghost" onClick={onCancel}>
            <X className="size-3.5" />
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={draft.trim().length === 0}
            onClick={() => onSave(draft.trim())}
          >
            <Check className="size-3.5" />
            Save
          </Button>
        </div>
      </div>
    </div>
  )
}
